import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useUserStats } from '../context/UserStatsContext';
import IntroductionCourse from '../components/IntroductionCourse';
import UserProfileIcon from '../components/UserProfileIcon';
import WelcomeAnimation from '../components/WelcomeAnimation';
import useWelcomeAnimation from '../hooks/useWelcomeAnimation';
import Logo from '../components/Logo';
import { getVersionString } from '../version';

const INTRO_COMPLETED_KEY = 'litrlly_intro_completed';

interface PracticeModeCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  textColor: string;
  bgColor: string;
  darkBgColor: string;
  hoverColor: string;
  onClick: () => void;
  badge?: string;
  badgeColor?: string;
}

const PracticeModeCard: React.FC<PracticeModeCardProps> = ({
  title,
  description,
  icon,
  color,
  textColor,
  bgColor,
  darkBgColor,
  hoverColor,
  onClick,
  badge,
  badgeColor = 'bg-gray-100 text-gray-600',
}) => {
  return (
    <button
      onClick={onClick}
      className={`${bgColor} ${darkBgColor} ${hoverColor} rounded-2xl p-6 text-left transition-all duration-200 transform hover:scale-105 hover:shadow-lg border-2 border-transparent hover:border-opacity-50 w-full`}
      style={{ borderColor: color }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${color.includes('blue') ? 'bg-blue-100 dark:bg-blue-900/50' : color.includes('purple') ? 'bg-purple-100 dark:bg-purple-900/50' : color.includes('amber') ? 'bg-amber-100 dark:bg-amber-900/50' : 'bg-emerald-100 dark:bg-emerald-900/50'}`}>
          {icon}
        </div>
        {badge && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${badgeColor}`}>
            {badge}
          </span>
        )}
      </div>
      <h3 className={`text-xl font-bold mb-2 ${textColor}`}>{title}</h3>
      <p className="text-gray-700 dark:text-gray-300 text-sm">{description}</p>
    </button>
  );
};

const HubPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { plan, getRecordingsRemaining, initializeSubscription } = useSubscription();
  const { stats } = useUserStats();
  const { showWelcome, completeWelcome } = useWelcomeAnimation();

  const [showIntro, setShowIntro] = useState<boolean>(() => {
    return !localStorage.getItem(INTRO_COMPLETED_KEY);
  });

  // Initialize subscription for new users
  useEffect(() => {
    if (currentUser) {
      initializeSubscription().catch(console.error);
    }
  }, [currentUser, initializeSubscription]);

  const remainingRecordings = getRecordingsRemaining();
  const isUnlimited = remainingRecordings === -1;

  const handleIntroComplete = () => {
    localStorage.setItem(INTRO_COMPLETED_KEY, 'true');
    setShowIntro(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Show introduction course for new users
  if (showIntro) {
    return <IntroductionCourse onComplete={handleIntroComplete} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-sm dark:shadow-gray-900 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Logo size="md" linkTo="/" />
          <div className="flex items-center gap-4">
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">Logout</span>
            </button>
            <UserProfileIcon />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Daily Recordings & Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900 p-4 mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            {/* Recordings */}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Today's Recordings</p>
                <p className="font-semibold text-gray-800 dark:text-gray-200">
                  {isUnlimited ? (
                    <span className="text-green-600">Unlimited</span>
                  ) : (
                    <span>{remainingRecordings}/3 left</span>
                  )}
                </p>
              </div>
            </div>

            {/* Streak */}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <span className="text-lg">🔥</span>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Current Streak</p>
                <p className="font-semibold text-gray-800 dark:text-gray-200">{stats?.currentStreak || 0} days</p>
              </div>
            </div>

            {/* Practice Time */}
            <div className="hidden sm:flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Practice</p>
                <p className="font-semibold text-gray-800 dark:text-gray-200">{stats?.totalPracticeMinutes || 0} min</p>
              </div>
            </div>
          </div>

          {/* Plan Badge & Upgrade */}
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              plan === 'free'
                ? 'bg-gray-100 text-gray-600'
                : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
            }`}>
              {plan === 'free' ? 'Free Plan' : plan.charAt(0).toUpperCase() + plan.slice(1)}
            </span>
            {plan === 'free' && (
              <Link
                to="/pricing"
                className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                Upgrade
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            )}
          </div>
        </div>

        {/* Practice Mode Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {/* Speech Practice */}
          <PracticeModeCard
            title="Speech Practice"
            description="Improve your pronunciation with AI-powered feedback on any text"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            }
            color="text-blue-600"
            textColor="text-blue-700 dark:text-blue-300"
            bgColor="bg-blue-50"
            darkBgColor="dark:bg-blue-900/30"
            hoverColor="hover:bg-blue-100 dark:hover:bg-blue-900/50"
            onClick={() => navigate('/app/speech')}
            badge={isUnlimited ? undefined : `${remainingRecordings} left`}
            badgeColor={remainingRecordings === 0 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300'}
          />

          {/* Interview Practice */}
          <PracticeModeCard
            title="Interview Prep"
            description="Practice job interviews with AI-generated questions and feedback"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
            color="text-purple-600"
            textColor="text-purple-700 dark:text-purple-300"
            bgColor="bg-purple-50"
            darkBgColor="dark:bg-purple-900/30"
            hoverColor="hover:bg-purple-100 dark:hover:bg-purple-900/50"
            onClick={() => navigate('/app/interview')}
            badge={isUnlimited ? undefined : `${remainingRecordings} left`}
            badgeColor={remainingRecordings === 0 ? 'bg-red-100 text-red-600' : 'bg-purple-100 text-purple-600 dark:bg-purple-900/50 dark:text-purple-300'}
          />

          {/* IELTS Practice */}
          <PracticeModeCard
            title="IELTS Practice"
            description="Prepare for IELTS with speaking, writing, reading & listening tests"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            }
            color="text-emerald-600"
            textColor="text-emerald-700 dark:text-emerald-300"
            bgColor="bg-emerald-50"
            darkBgColor="dark:bg-emerald-900/30"
            hoverColor="hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
            onClick={() => navigate('/app/ielts')}
            badge={isUnlimited ? undefined : `${remainingRecordings} left`}
            badgeColor={remainingRecordings === 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-300'}
          />

          {/* Practice Sounds */}
          <PracticeModeCard
            title="Practice Sounds"
            description="Target specific sounds like TH, R, L and vowels with focused exercises"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            }
            color="text-amber-600"
            textColor="text-amber-700 dark:text-amber-300"
            bgColor="bg-amber-50"
            darkBgColor="dark:bg-amber-900/30"
            hoverColor="hover:bg-amber-100 dark:hover:bg-amber-900/50"
            onClick={() => navigate('/app/sounds')}
            badge={isUnlimited ? undefined : `${remainingRecordings} left`}
            badgeColor={remainingRecordings === 0 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-300'}
          />
        </div>

        {/* Feature Descriptions */}
        <div className="space-y-6 mb-10">
          {/* Pronunciation AI Practice */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900 p-6 border-l-4 border-blue-500">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">Pronunciation AI Practice</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Master your pronunciation with our advanced AI-powered speech analysis. Simply type or paste any text,
                  record yourself reading it, and receive instant feedback on your pronunciation accuracy.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Word-by-word pronunciation scoring
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Detailed phoneme-level analysis
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Practice with custom texts or AI-generated content
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Interview Emulation */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900 p-6 border-l-4 border-purple-500">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">Full Interview Emulation</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Experience realistic job interview simulations powered by AI. Our system generates role-specific questions,
                  listens to your responses, and provides comprehensive feedback to help you ace your next interview.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Industry-specific interview questions
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    AI-powered follow-up questions based on your answers
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Detailed feedback on content, delivery, and communication
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* IELTS Test Emulation */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900 p-6 border-l-4 border-emerald-500">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">Full IELTS Test Emulation</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Prepare for your IELTS exam with complete test simulations covering all four sections.
                  Practice under real exam conditions and receive band score estimates with detailed feedback.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Speaking: 3-part interview simulation with AI examiner
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Writing: Task 1 & Task 2 with scoring on all criteria
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Reading: Academic passages with various question types
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Listening: Audio-based sections with real exam format
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Practice Sounds */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900 p-6 border-l-4 border-amber-500">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">Targeted Sound Practice</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Focus on the specific sounds that challenge you most. Our intelligent system identifies your problem areas
                  and provides targeted exercises to help you master difficult English sounds like TH, R, L, and vowels.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    8 sound categories: TH, R, L, vowels, schwa, clusters & more
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Personalized recommendations based on your performance
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Quick practice mode for spontaneous training sessions
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Limit Warning */}
        {remainingRecordings === 0 && plan === 'free' && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-300">Daily limit reached</h3>
                <p className="text-yellow-700 dark:text-yellow-400 text-sm mt-1">
                  You've used all 3 free recordings for today. Upgrade to get unlimited practice!
                </p>
                <Link
                  to="/pricing"
                  className="inline-block mt-3 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-lg transition-colors"
                >
                  Upgrade Now
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Quick Tips */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900 p-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Quick Tips</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🎯</span>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white">Practice Daily</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Consistency beats intensity</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🗣️</span>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white">Speak Clearly</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Enunciate each word</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔄</span>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white">Review Feedback</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Learn from your mistakes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-gray-400 dark:text-gray-500">
          <div className="flex justify-center gap-4 mb-2">
            <Link to="/pricing" className="hover:text-gray-600 dark:hover:text-gray-300">Pricing</Link>
            <Link to="/contact" className="hover:text-gray-600 dark:hover:text-gray-300">Contact</Link>
            <Link to="/privacy" className="hover:text-gray-600 dark:hover:text-gray-300">Privacy</Link>
          </div>
          <p className="text-xs text-gray-300 dark:text-gray-600">{getVersionString()}</p>
        </footer>
      </div>

      {/* Welcome Animation */}
      {showWelcome && (
        <WelcomeAnimation
          userName={currentUser?.email || undefined}
          onComplete={completeWelcome}
        />
      )}
    </div>
  );
};

export default HubPage;
